import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDashboard,
  faBolt,
  faArrowTrendUp,
  faCertificate,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';
import useMouseParallax from '../../hooks/useMouseParallax';
import ParticleCanvas from './ParticleCanvas';
import Badge from '../common/Badge';
import Button from '../common/Button';
import { transitions, revealStagger, floatPhysics } from '../../utils/motion';

const TITLE_WORDS = ['Invest', 'In', 'Art,', 'Powered', 'By', 'Innovation'];

const TICKER_ITEMS = [
  'TitTat #01 "Sovereign Heritage" fractional trading volume +32.4%',
  'Live Auction: 03m 48s remaining on Session #104',
  'Verified Provenance: 100/100 Fractional Tokens Sealed',
  'Sharded Matching Engine: Sub-10ms Execution',
  '12 New Contemporary Masterworks Listed in Sovereign Vault',
];

export default function HeroSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();
  const [parallaxRef, , offset] = useMouseParallax(0.015);
  const [tickerIndex, setTickerIndex] = useState(0);

  // Scroll-linked exit transformations for the hero showcase card
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const cardScrollY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, shouldReduceMotion ? 0 : 80]
  );
  const cardScrollRotate = useTransform(
    scrollYProgress,
    [0, 1],
    [0, shouldReduceMotion ? 0 : 7]
  );
  const cardScrollScale = useTransform(
    scrollYProgress,
    [0, 1],
    [1, shouldReduceMotion ? 1 : 0.94]
  );
  const cardScrollOpacity = useTransform(scrollYProgress, [0, 0.85, 1], [1, 0.9, 0.4]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTickerIndex((prev) => (prev + 1) % TICKER_ITEMS.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="lp-hero" ref={heroRef}>
      <div ref={parallaxRef} style={{ width: '100%', height: '100%', position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <ParticleCanvas />

        {/* Floating mathematical shapes — parallax layers */}
        <div
          className="lp-float-shape lp-float-shape--circle"
          style={{ transform: `translate(${offset.x * 1.8}px, ${offset.y * 1.8}px)` }}
          aria-hidden="true"
        />
        <div
          className="lp-float-shape lp-float-shape--triangle"
          style={{ transform: `translate(${offset.x * -1.2}px, ${offset.y * -1.2}px)` }}
          aria-hidden="true"
        />
        <div
          className="lp-float-shape lp-float-shape--square"
          style={{ transform: `rotate(45deg) translate(${offset.x * 2}px, ${offset.y * 2}px)` }}
          aria-hidden="true"
        />
      </div>

      {/* Dashboard quick-access icon for logged-in users */}
      {user && (
        <Link
          to={`/${user.role}/dashboard`}
          className="lp-dashboard-icon"
          title="Go to Dashboard"
          aria-label="Go to Dashboard"
        >
          <FontAwesomeIcon icon={faDashboard} />
        </Link>
      )}

      <div className="lp-hero-layout">
        {/* Left Column: Headlines & Call to Actions */}
        <motion.div
          className="lp-hero-inner"
          variants={revealStagger.container}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={revealStagger.item}>
            <Badge variant="cyan" pulsing size="sm" style={{ marginBottom: '1.25rem', letterSpacing: '0.12em' }}>
              DECENTRALIZED FINE ART EXCHANGE
            </Badge>
          </motion.div>

          <h1 className="lp-hero-title">
            {TITLE_WORDS.map((word, i) => (
              <motion.span
                key={i}
                className={`lp-word${word === 'Art,' ? ' lp-gradient-text' : ''}`}
                variants={revealStagger.item}
              >
                {word}{' '}
              </motion.span>
            ))}
          </h1>

          <motion.p className="lp-hero-subtitle" variants={revealStagger.item}>
            Own fractions of curated fine art assets. Experience collective liquidity,
            algorithmic price discovery, and real-time live auctions powered by modern mathematical market architecture.
          </motion.p>

          <motion.div className="lp-hero-actions" variants={revealStagger.item}>
            <Button
              variant="primary"
              size="lg"
              trailingIcon="→"
              onClick={() => navigate('/signup')}
            >
              Get Started
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => {
                const el = document.getElementById('curated-vault');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Explore Collection
            </Button>
          </motion.div>
        </motion.div>

        {/* Right Column: Kinetic 3D Showcase Card with Scroll-Linked Physics */}
        <motion.div
          className="lp-hero-showcase"
          style={{
            y: cardScrollY,
            rotateZ: cardScrollRotate,
            scale: cardScrollScale,
            opacity: cardScrollOpacity,
          }}
        >
          <motion.div
            className="lp-showcase-card"
            variants={floatPhysics}
            animate="animate"
            whileHover={{ scale: 1.02, rotateY: 3, rotateX: -3 }}
            style={{
              transform: `translate(${offset.x * 0.8}px, ${offset.y * 0.8}px)`,
            }}
          >
            {/* Ambient Radial Spotlight Behind Card */}
            <div className="lp-showcase-spotlight" />

            {/* Floating Top Badge */}
            <div className="lp-showcase-header">
              <span className="lp-showcase-pill-live">
                <span className="lp-pill-dot" /> LIVE AUCTION
              </span>
              <span className="lp-showcase-tittat-tag font-mono">TitTat #01 • Fractionalized</span>
            </div>

            {/* Artwork Presentation Canvas with Local Master Asset */}
            <div className="lp-showcase-image-wrap">
              <img
                src="/image_1.png"
                alt="The Sovereign Heritage"
                className="lp-showcase-img"
              />
              <div className="lp-showcase-glass-badge">
                <FontAwesomeIcon icon={faCertificate} className="lp-badge-icon" style={{ color: '#06b6d4' }} />
                <span>Provenance Verified</span>
              </div>
            </div>

            {/* Live Metrics Grid Inside Floating Card */}
            <div className="lp-showcase-details">
              <div className="lp-showcase-title-row">
                <h3 className="lp-showcase-title">The Sovereign Heritage</h3>
                <span className="lp-showcase-gain font-mono">
                  <FontAwesomeIcon icon={faArrowTrendUp} /> +28.6%
                </span>
              </div>
              <p className="lp-showcase-artist">By Master Mahendra Raj Shrestha • Oil on Linen</p>

              <div className="lp-showcase-stats-panel">
                <div className="lp-stat-box">
                  <span className="lp-stat-label">Asset Valuation</span>
                  <span className="lp-stat-number font-mono">NPR 3,850,000</span>
                </div>
                <div className="lp-stat-box">
                  <span className="lp-stat-label">Fractional Tokens</span>
                  <span className="lp-stat-number font-mono">100 / 100 Minted</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Dynamic Real-Time Ticker Bar */}
      <div className="lp-hero-ticker">
        <div className="lp-ticker-label">
          <FontAwesomeIcon icon={faBolt} className="lp-ticker-icon" />
          <span>MARKET TICKER</span>
        </div>
        <div className="lp-ticker-stream">
          <motion.span
            key={tickerIndex}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={transitions.smooth}
            className="lp-ticker-text font-mono"
          >
            {TICKER_ITEMS[tickerIndex]}
          </motion.span>
        </div>
      </div>
    </section>
  );
}

